import React, { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { decodeBusinessId } from "../utils/qr-generator";

const QRProtectedRoute = ({ element: Component }) => {
  const { token } = useParams();
  const [isValidToken, setIsValidToken] = useState(null); // null = loading, true = valid, false = invalid
  const [decodedBusinessId, setDecodedBusinessId] = useState(null);

  useEffect(() => {
    const validateQRToken = async () => {
      try {
        // Validate token against your backend or use a predefined valid token
        const isValid = await validateToken(token);
        setIsValidToken(isValid);
      } catch (error) {
        // console.error("Token validation error:", error);
        setIsValidToken(false);
      }
    };

    if (token) {
      validateQRToken();
    } else {
      setIsValidToken(false);
    }
  }, [token]);

  // Token validation function
  const validateToken = async (token) => {
    // console.log("🔍 Validating token:", token);
    
    // Option A: Validate against backend (recommended for production)
    try {
      // Uncomment when you have backend validation
      // const response = await fetch(`/api/validate-qr-token/${token}`);
      // return response.ok;
    } catch (error) {
      console.error('Backend validation error:', error);
    }
    
    // Option B: Client-side validation for now
    // Check if token matches our generated pattern
    if (!token || typeof token !== 'string') {
      console.log("❌ Token is invalid: not a string or empty");
      return false;
    }
    
    // Accept different types of tokens:
    // 1. Try to decode as encoded business ID
    const decodedId = decodeBusinessId(token);
    if (decodedId) {
      // console.log("✅ Token is encoded business ID, decoded to:", decodedId);
      setDecodedBusinessId(decodedId);
      return true;
    }
    
    // 2. Business profile IDs (MongoDB ObjectIds - 24 character hex string)
    const mongoIdPattern = /^[0-9a-fA-F]{24}$/;
    
    // 3. Legacy generated tokens
    const tokenPattern = /^business-reg-\d+-[a-z0-9]+$|^batch-\d+-business-reg-\d+-[a-z0-9]+$/;
    
    // 4. Predefined tokens
    const predefinedTokens = [
      "business-reg-2024",
      "qr-access-token",
      "test123",
      "507f1f77bcf86cd799439011",
    ];
    
    const isMongoId = mongoIdPattern.test(token);
    const isLegacyToken = tokenPattern.test(token);
    const isPredefinedToken = predefinedTokens.includes(token);
    
    if (isMongoId) {
      setDecodedBusinessId(token); // If it's already a MongoDB ID, use it directly
    }
    
    // console.log("🔎 Token validation results:", {
    //   token,
    //   isEncodedBusinessId: !!decodedId,
    //   isMongoId,
    //   isLegacyToken,
    //   isPredefinedToken,
    //   decodedBusinessId: decodedId,
    //   valid: !!decodedId || isMongoId || isLegacyToken || isPredefinedToken
    // });
    
    return !!decodedId || isMongoId || isLegacyToken || isPredefinedToken;
  };

  // Show loading while validating
  if (isValidToken === null) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>Verifying access...</div>
      </div>
    );
  }

  // Redirect if token is invalid
  if (!isValidToken) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f8f9fa",
          padding: "20px",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            padding: "30px",
            borderRadius: "10px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            textAlign: "center",
            maxWidth: "400px",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "20px" }}>🚫</div>
          <h3 style={{ color: "#dc3545", marginBottom: "15px" }}>Invalid QR Code</h3>
          <p style={{ color: "#6c757d", marginBottom: "20px" }}>
            This QR code is invalid or has expired. Please contact the business owner for a valid QR code.
          </p>
          <div style={{ fontSize: "12px", color: "#adb5bd" }}>
            Token: <code>{token}</code>
          </div>
        </div>
      </div>
    );
  }

  // Render component if token is valid
  return <Component token={token} businessId={decodedBusinessId} />;
};

export default QRProtectedRoute;
